/**
 * Organization Service
 * Handles organization management API calls
 */
import apiClient from './apiClient';

export interface Organization {
  id: number;
  uuid: string;
  name: string;
  description: string;
  organization_code: string;
  created_at: string;
  updated_at: string;
  is_default: boolean;
}

export interface CreateOrganizationRequest {
  name: string;
  description?: string;
}

export interface InviteUserRequest {
  email: string;
  first_name?: string;
  last_name?: string;
}

export interface CreateUserWithPasswordRequest {
  email: string;
  first_name?: string;
  last_name?: string;
  temporary_password: string;
}

export type SafeguardDirection = 'source' | 'target';
export type DialpadEntityBucket = 'users' | 'offices' | 'departments';

export interface PlatformSafeguard {
  id: number;
  uuid: string;
  organization: number;
  platform: number;
  platform_type: string;
  direction: SafeguardDirection;
  hard_limit_enabled: boolean;
  entity_caps: Partial<Record<DialpadEntityBucket, number>>;
  default_office_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UpdatePlatformSafeguardRequest {
  hard_limit_enabled?: boolean;
  entity_caps?: Partial<Record<DialpadEntityBucket, number>>;
  default_office_id?: string;
  is_active?: boolean;
}

class OrganizationService {
  /**
   * Get current user's organization
   */
  async getCurrentOrganization(): Promise<Organization> {
    const response = await apiClient.get('/api/organizations/current/');
    return response.data;
  }

  /**
   * Get organization by ID
   */
  async getOrganization(id: number): Promise<Organization> {
    const response = await apiClient.get(`/api/organizations/${id}/`);
    return response.data;
  }

  /**
   * Update organization details
   */
  async updateOrganization(id: number, data: Partial<Organization>): Promise<Organization> {
    const response = await apiClient.patch(`/api/organizations/${id}/`, data);
    return response.data;
  }

  /**
   * Invite user to organization (email only - sends temp password via email)
   */
  async inviteUser(data: InviteUserRequest): Promise<{ message: string; email: string }> {
    const response = await apiClient.post('/api/organizations/invite-user/', data);
    return response.data;
  }

  /**
   * Create user with temporary password (admin creates account directly)
   */
  async createUserWithPassword(data: CreateUserWithPasswordRequest): Promise<{
    message: string;
    email: string;
    temporary_password: string;
  }> {
    const response = await apiClient.post('/api/organizations/create-user/', data);
    return response.data;
  }

  /**
   * Verify organization code exists
   */
  async verifyOrganizationCode(code: string): Promise<{ valid: boolean; organization_name?: string }> {
    const response = await apiClient.post('/api/organizations/verify-code/', { code });
    return response.data;
  }

  /**
   * Create new organization (superuser only)
   */
  async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    const response = await apiClient.post('/api/organizations/create/', data);
    return response.data;
  }

  /**
   * List all organizations (superuser only)
   */
  async listAllOrganizations(): Promise<Organization[]> {
    const response = await apiClient.get('/api/organizations/all/');
    return response.data;
  }

  /**
   * Switch to different organization (superuser only)
   */
  async switchOrganization(organizationId: number): Promise<{ message: string; organization: Organization }> {
    const response = await apiClient.post('/api/organizations/switch/', { organization_id: organizationId });
    return response.data;
  }

  async getCurrentPlatformSafeguard(platformType: string): Promise<PlatformSafeguard | null> {
    try {
      const response = await apiClient.get(`/api/organizations/current/platform-safeguards/${platformType}/`);
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async updateCurrentPlatformSafeguard(
    platformType: string,
    data: UpdatePlatformSafeguardRequest,
  ): Promise<PlatformSafeguard> {
    const response = await apiClient.patch(
      `/api/organizations/current/platform-safeguards/${platformType}/`,
      data,
    );
    return response.data;
  }
}

export default new OrganizationService();
